import { AnalyticsPeriod } from './types';

/**
 * (De)serialize AnalyticsPeriod to a URL-safe string so Analytics dashboards
 * are shareable by URL.
 *
 * Format: `<mode>:<arg1>|<arg2>|…`
 *  - dateRange:2026-01-01|2026-03-31|thisMonth?
 *  - sprint:123|CORE-2026.03.02
 *  - release:WBE 1.26.03
 *  - quarter:2026-Q1
 *
 * Legacy `custom:...` links are silently promoted to `dateRange:...` at decode
 * time so already-shared URLs keep working after the Custom mode was removed.
 */

const SEP = '|';

export function encodePeriod(p: AnalyticsPeriod | null): string {
  if (!p) return '';
  switch (p.mode) {
    case 'dateRange':
      return `dateRange:${encodeURIComponent(p.startDate)}${SEP}${encodeURIComponent(p.endDate)}${
        p.presetKey ? SEP + encodeURIComponent(p.presetKey) : ''
      }`;
    case 'sprint':
      return `sprint:${encodeURIComponent(p.sprintId)}${SEP}${encodeURIComponent(p.sprintName)}`;
    case 'release':
      return `release:${encodeURIComponent(p.fixVersion)}`;
    case 'quarter':
      return `quarter:${encodeURIComponent(p.quarter)}`;
    default:
      return '';
  }
}

export function decodePeriod(raw: string | null | undefined): AnalyticsPeriod | null {
  if (!raw) return null;
  const idx = raw.indexOf(':');
  if (idx < 0) return null;
  const mode = raw.slice(0, idx);
  const rest = raw.slice(idx + 1);
  const parts = rest.split(SEP).map((s) => decodeURIComponent(s));
  switch (mode) {
    case 'dateRange':
      if (parts.length < 2 || !parts[0] || !parts[1]) return null;
      return {
        mode: 'dateRange',
        startDate: parts[0],
        endDate: parts[1],
        presetKey: parts[2] || undefined,
      };
    case 'sprint':
      if (parts.length < 2 || !parts[0]) return null;
      return { mode: 'sprint', sprintId: parts[0], sprintName: parts[1] || parts[0] };
    case 'release':
      if (!parts[0]) return null;
      return { mode: 'release', fixVersion: parts[0] };
    case 'quarter':
      if (!parts[0]) return null;
      return { mode: 'quarter', quarter: parts[0] };
    // Back-compat for URLs shared before Custom mode was removed.
    case 'custom':
      if (parts.length < 2 || !parts[0] || !parts[1]) return null;
      return {
        mode: 'dateRange',
        startDate: parts[0],
        endDate: parts[1],
      };
    default:
      return null;
  }
}

/** Back-compat: older links used ?startDate=&endDate=. Promote to dateRange period. */
export function decodeLegacyDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): AnalyticsPeriod | null {
  if (!startDate || !endDate) return null;
  return { mode: 'dateRange', startDate, endDate };
}
